import { useEffect, useState } from "react";

import { supabase } from "~/integrations/supabase/client";

interface ProductDetails {
  weight_kg?: number;
  volume_cm3?: number;
}

// Global cache for product details to prevent duplicate requests across components
const globalProductCache = new Map<string, ProductDetails>();
const globalFetchedIds = new Set<string>();

export const useProductDetails = (productIds: string[]) => {
  const [productDetails, setProductDetails] = useState<
    Record<string, ProductDetails>
  >({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (productIds.length === 0) {
      return;
    }

    // Check what we already have in the global cache
    const cachedDetails: Record<string, ProductDetails> = {};
    const newProductIds: string[] = [];

    productIds.forEach((id) => {
      if (globalProductCache.has(id)) {
        cachedDetails[id] = globalProductCache.get(id)!;
      } else if (!globalFetchedIds.has(id)) {
        newProductIds.push(id);
      }
    });

    // Update local state with cached data
    if (Object.keys(cachedDetails).length > 0) {
      setProductDetails((prev) => ({ ...prev, ...cachedDetails }));
    }

    // Only fetch product IDs that we haven't fetched before
    if (newProductIds.length > 0) {
      fetchProductDetails(newProductIds);
    }
  }, [productIds.join(",")]); // Use string representation for stable comparison

  const fetchProductDetails = async (ids: string[]) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("products")
        .select("id, weight_kg, volume_cm3")
        .in("id", ids);

      if (error) {
        console.error("Error fetching product details:", error);
        return;
      }

      const details: Record<string, ProductDetails> = {};
      data?.forEach((product) => {
        details[product.id] = {
          weight_kg: product.weight_kg,
          volume_cm3: product.volume_cm3,
        };
      });

      // Update global cache
      Object.entries(details).forEach(([id, detail]) => {
        globalProductCache.set(id, detail);
      });

      setProductDetails((prev) => ({
        ...prev,
        ...details,
      }));

      // Track which IDs we've fetched globally
      ids.forEach((id) => globalFetchedIds.add(id));
    } catch (error) {
      console.error("Failed to fetch product details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return { productDetails, isLoading };
};
